import { PendingPublicationList } from "../components/PendingPublicationList";
import { PublicationTrackingCard } from "@/components/PublicationTrackingCard";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import {
  AlertCircle,
  ArrowLeft,
  CalendarClock,
  Check,
  Copy,
  ExternalLink,
  FileCode2,
  Loader2,
  Pencil,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Shuffle,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { normalizeArticleWorkbenchTopicBriefPack } from "../../../shared/articleWorkbenchBriefPack";
import { eligibleTopicKeywords, type PlanningKeyword } from "../../../shared/articleTopicPlanning";
import ArticleTopicPlanningInfo from "@/components/ArticleTopicPlanningInfo";

type WorkbenchTopic = {
  id: number;
  title: string;
  mainKeyword: string;
  targetPage?: string | null;
  emphasis?: string | null;
  nextAction?: string | null;
  topicBriefPack?: string | null;
  sourceType: string;
  sourceKeywordId?: number | null;
  sourceKeyword?: string | null;
  sourceKeywordCategory?: string | null;
  status: string;
  publicationUrl?: string | null;
  publicationTrackingJson?: string | null;
  publishedAt?: string | Date | null;
  citationSummary?: {
    statusLabel: string;
    lastCheckedAt?: string | Date | null;
    platforms?: Array<{
      provider: string;
      sourceType: string;
      count: number;
      latestAt?: string | Date | null;
    }>;
    latestExcerpt?: string | null;
    consecutiveCitedRuns?: number;
    consecutiveMissedRuns?: number;
  };
  suggestedAt: string | Date;
  updatedAt: string | Date;
};

type WorkbenchOutput = {
  id: number;
  topicId: number;
  version: number;
  title: string;
  htmlContent: string;
  schemaJson: string;
  knowledgeSyncRunId?: string | null;
  knowledgeCardIdsJson?: string | null;
  knowledgeCardEvidenceJson?: string | null;
  createdAt: string | Date;
};

type WorkbenchKnowledgeEvidence = {
  id: string;
  title: string;
  sourceVideo: string;
  sourceTime: string;
  sourceSegments: string[];
  sourceEvidence: Array<{
    segment: string;
    sourceVideo: string;
    sourceTime: string;
    sourcePath: string;
  }>;
  sourcePath: string;
};

export type WorkbenchKnowledgeEvidenceParseResult =
  | { status: "valid"; evidence: WorkbenchKnowledgeEvidence[] }
  | { status: "invalid"; evidence: [] };

function isWorkbenchKnowledgeEvidence(
  value: unknown
): value is WorkbenchKnowledgeEvidence {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<WorkbenchKnowledgeEvidence>;
  return (
    typeof item.id === "string" &&
    Boolean(item.id.trim()) &&
    typeof item.title === "string" &&
    Boolean(item.title.trim()) &&
    typeof item.sourceVideo === "string" &&
    Boolean(item.sourceVideo.trim()) &&
    typeof item.sourceTime === "string" &&
    Boolean(item.sourceTime.trim()) &&
    Array.isArray(item.sourceSegments) &&
    item.sourceSegments.length > 0 &&
    item.sourceSegments.every(
      segment => typeof segment === "string" && Boolean(segment.trim())
    ) &&
    Array.isArray(item.sourceEvidence) &&
    item.sourceEvidence.length === item.sourceSegments.length &&
    item.sourceEvidence.every(
      (evidence, index) =>
        evidence &&
        typeof evidence === "object" &&
        typeof evidence.segment === "string" &&
        evidence.segment.trim() === item.sourceSegments?.[index] &&
        typeof evidence.sourceVideo === "string" &&
        Boolean(evidence.sourceVideo.trim()) &&
        typeof evidence.sourceTime === "string" &&
        Boolean(evidence.sourceTime.trim()) &&
        typeof evidence.sourcePath === "string" &&
        Boolean(evidence.sourcePath.trim())
    ) &&
    typeof item.sourcePath === "string" &&
    Boolean(item.sourcePath.trim())
  );
}

export function parseWorkbenchKnowledgeEvidence(
  output: WorkbenchOutput | null
): WorkbenchKnowledgeEvidenceParseResult {
  if (!output) return { status: "valid", evidence: [] };
  if (
    typeof output.knowledgeCardEvidenceJson !== "string" ||
    !output.knowledgeCardEvidenceJson.trim()
  ) {
    return { status: "invalid", evidence: [] };
  }
  try {
    const parsed = JSON.parse(output.knowledgeCardEvidenceJson);
    if (!Array.isArray(parsed) || !parsed.every(isWorkbenchKnowledgeEvidence)) {
      return { status: "invalid", evidence: [] };
    }
    return { status: "valid", evidence: parsed };
  } catch {
    return { status: "invalid", evidence: [] };
  }
}

function formatSchemaScript(schemaJson: string) {
  const trimmed = schemaJson.trim();
  if (/^<script\b/i.test(trimmed)) return trimmed;
  return `<script type="application/ld+json">\n${trimmed}\n</script>`;
}

type WorkbenchGenerationLog = {
  id: number;
  topicId: number;
  outputVersion: number;
  stepKey: string;
  stepTitle: string;
  status: "active" | "done" | "warn" | "error";
  message: string;
  createdAt: string | Date;
};

type WorkbenchGenerationDraft = {
  id: number;
  topicId: number;
  outputVersion: number;
  status: "drafting" | "error" | "complete";
  currentStep: string;
  errorMessage?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
};

type TrackingKeywordOption = PlanningKeyword & {
  category?: string | null;
};

type ConfirmationRowKey =
  | "sourceKeyword"
  | "person"
  | "scene"
  | "product"
  | "brandInfo"
  | "purchaseRisk"
  | "decisionPath"
  | "coreProblem"
  | "firstJudgment"
  | "entryPage"
  | "faqDirections";

type ConfirmationRow = {
  key: ConfirmationRowKey;
  label: string;
  value: string;
  editable?: boolean;
};

const ACTIVE_STATUSES = [
  "suggested",
  "brief_confirmed",
  "drafting",
];
const TRACKING_STATUSES = [
  "tracking",
  "cited",
  "citation_lost",
  "update_suggested",
];

const STATUS_LABELS: Record<string, string> = {
  suggested: "待確認",
  brief_confirmed: "待確認",
  drafting: "準備中",
  ready_to_publish: "待發布",
  tracking: "追蹤中",
  cited: "已引用",
  citation_lost: "引用流失",
  update_suggested: "建議更新",
  archived: "已封存",
};

function generationStepLabel(step: string) {
  const section = step.match(/^section_(\d+)$/);
  if (section) return `第 ${section[1]} 章`;
  return (
    (
      {
        product_feed: "商品配對",
        external_knowledge: "外部知識查證",
        brief: "讀者問題整理",
        chapter_template: "五章順序",
        planning: "文章大綱",
        intro: "文章前言",
        table: "判斷清單",
        title: "文章標題",
        enrichment: "FAQ 與 Schema",
        speak_human: "校對台灣繁體中文語感",
        complete: "完成",
      } as Record<string, string>
    )[step] || "未完成階段"
  );
}

export function resolveResumingArticleGenerationDraft(
  draft: WorkbenchGenerationDraft | null | undefined,
  resumedDraft: WorkbenchGenerationDraft | null
) {
  // Only the exact pre-resume error is stale. A new server update is authoritative.
  if (
    resumedDraft &&
    draft?.status === "error" &&
    draft.id === resumedDraft.id &&
    draft.outputVersion === resumedDraft.outputVersion &&
    new Date(draft.updatedAt).getTime() ===
      new Date(resumedDraft.updatedAt).getTime()
  ) {
    return { ...draft, status: "drafting" as const, errorMessage: null };
  }
  return draft;
}

export function isArticleGenerationWaiting(input: {
  generationStarted: boolean;
  expectedOutputVersion?: number | null;
  draftStatus?: WorkbenchGenerationDraft["status"] | null;
  draftOutputVersion?: number | null;
  hasOutput: boolean;
}) {
  if (input.hasOutput) return false;
  if (input.generationStarted) {
    if (
      !isArticleGenerationVersionMatch(
        input.expectedOutputVersion,
        input.draftOutputVersion
      )
    ) {
      return true;
    }
    return input.draftStatus !== "error" && input.draftStatus !== "complete";
  }
  if (input.draftStatus === "error" || input.draftStatus === "complete")
    return false;
  return input.draftStatus === "drafting";
}

export function shouldPollArticleGenerationDraft(input: {
  requestPending: boolean;
  generationStarted: boolean;
  expectedOutputVersion?: number | null;
  topicStatus?: string | null;
  draftStatus?: WorkbenchGenerationDraft["status"] | null;
  draftOutputVersion?: number | null;
}) {
  const isMatchingTerminalDraft =
    (input.draftStatus === "error" || input.draftStatus === "complete") &&
    isArticleGenerationVersionMatch(
      input.expectedOutputVersion,
      input.draftOutputVersion
    );
  if (isMatchingTerminalDraft) return false;
  if (input.requestPending) return true;
  if (input.generationStarted) {
    if (
      !isArticleGenerationVersionMatch(
        input.expectedOutputVersion,
        input.draftOutputVersion
      )
    ) {
      return true;
    }
    return input.draftStatus !== "error" && input.draftStatus !== "complete";
  }
  if (input.draftStatus === "error" || input.draftStatus === "complete")
    return false;
  return input.topicStatus === "drafting" || input.draftStatus === "drafting";
}

export function shouldPollArticleGenerationOutput(input: {
  generationStarted: boolean;
  expectedOutputVersion?: number | null;
  draftStatus?: WorkbenchGenerationDraft["status"] | null;
  draftOutputVersion?: number | null;
  hasOutput: boolean;
}) {
  if (!input.generationStarted || input.hasOutput) return false;
  const isCurrentErrorDraft =
    input.draftStatus === "error" &&
    isArticleGenerationVersionMatch(
      input.expectedOutputVersion,
      input.draftOutputVersion
    );
  return !isCurrentErrorDraft;
}

export function isArticleGenerationVersionMatch(
  expectedOutputVersion?: number | null,
  actualOutputVersion?: number | null
) {
  if (!expectedOutputVersion) return true;
  return Number(actualOutputVersion || 0) === expectedOutputVersion;
}

export function getArticleGenerationCurrentStep(
  expectedOutputVersion: number | null,
  draftOutputVersion?: number | null,
  currentStep?: string | null
) {
  if (
    !isArticleGenerationVersionMatch(expectedOutputVersion, draftOutputVersion)
  )
    return null;
  return currentStep || null;
}

export function resolveArticleGenerationVersion(
  expectedOutputVersion: number | null,
  draft?: Pick<WorkbenchGenerationDraft, "outputVersion"> | null
) {
  const draftOutputVersion = Number(draft?.outputVersion || 0);
  if (!draftOutputVersion) return expectedOutputVersion;
  if (!expectedOutputVersion || draftOutputVersion >= expectedOutputVersion)
    return draftOutputVersion;
  return expectedOutputVersion;
}

export function selectArticleGenerationLogs(
  logs: WorkbenchGenerationLog[],
  expectedOutputVersion: number | null,
  generationStartedAt: number | null,
  requestPending: boolean
) {
  if (expectedOutputVersion) {
    return logs.filter(
      log => Number(log.outputVersion || 0) === expectedOutputVersion
    );
  }
  if (!generationStartedAt || !requestPending) return logs;
  return logs.filter(
    log => new Date(log.createdAt).getTime() >= generationStartedAt - 3000
  );
}

export function getArticleGenerationOutputCreatedAfter(
  generationStartedAt: number | null,
  expectedOutputVersion: number | null
) {
  if (!generationStartedAt || expectedOutputVersion) return undefined;
  return new Date(generationStartedAt - 3000).toISOString();
}

export function getNextArticleGenerationVersion(
  ...knownVersions: Array<number | null | undefined>
) {
  return Math.max(0, ...knownVersions.map(version => Number(version || 0))) + 1;
}

export function getEmptyGenerationProgressMessage(
  currentStep?: string | null,
  failed = false
) {
  if (!currentStep) return "正在建立產文任務";
  const step = generationStepLabel(currentStep);
  return failed ? `系統錯誤：停在${step}` : `正在處理：${step}`;
}

export function shouldShowArticleGenerationProgress(input: {
  waiting: boolean;
  hasOutput: boolean;
  draftError: boolean;
}) {
  if (input.draftError) return true;
  if (input.hasOutput) return false;
  return input.waiting;
}

type GenerationIssue = {
  title: string;
  message: string;
  actionLabel: string;
};

function isAbortLikeError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  return /aborted|AbortError|signal is aborted/i.test(message);
}

export function isOutputFreshForCurrentGeneration(
  output: Pick<WorkbenchOutput, "version" | "createdAt">,
  generationStartedAt: number | null,
  expectedOutputVersion: number | null,
  draftComplete = false
) {
  if (expectedOutputVersion) {
    const outputVersion = Number(output.version || 0);
    return (
      outputVersion === expectedOutputVersion ||
      (draftComplete &&
        !generationStartedAt &&
        outputVersion > expectedOutputVersion)
    );
  }
  if (!generationStartedAt) return true;
  const createdAt = new Date(output.createdAt).getTime();
  if (Number.isNaN(createdAt)) return false;
  return createdAt >= generationStartedAt - 3000;
}

function formatDate(value?: string | Date | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getCitationStatus(topic: WorkbenchTopic) {
  const summary = topic.citationSummary;
  if (!topic.publicationUrl) {
    return {
      label: "尚未發布",
      description: "貼上官網文章網址後才會列入引用追蹤。",
      tone: "muted",
    };
  }

  if (!summary?.lastCheckedAt) {
    return {
      label: "尚未檢查",
      description: "文章已列入追蹤，還沒有引用檢查紀錄。",
      tone: "muted",
    };
  }

  if (summary.statusLabel === "已引用") {
    const platforms = summary.platforms
      ?.map(item => item.provider)
      .filter(Boolean);
    return {
      label: "已引用",
      description: platforms?.length
        ? `最近引用來源：${Array.from(new Set(platforms)).join("、")}`
        : "最近檢查有引用到這篇文章。",
      tone: "success",
    };
  }

  return {
    label: "尚未引用",
    description: `最後檢查：${formatDate(summary.lastCheckedAt)}`,
    tone: "muted",
  };
}

function statusBadgeClass(tone: string) {
  if (tone === "success")
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (tone === "warning") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-border bg-muted/50 text-muted-foreground";
}

function buildConfirmationRows(topic: WorkbenchTopic) {
  const briefPack = normalizeArticleWorkbenchTopicBriefPack(
    topic.topicBriefPack
  );
  const matchedProducts = briefPack.matchedProducts;
  return [
    {
      key: "sourceKeyword",
      label: "來源關鍵字",
      value: topic.sourceKeyword || topic.mainKeyword,
    },
    {
      key: "person",
      label: "人",
      value: briefPack.person || briefPack.audience,
    },
    {
      key: "scene",
      label: "場",
      value:
        briefPack.scene || briefPack.scenarios.join("、") || topic.mainKeyword,
    },
    {
      key: "product",
      label: "貨",
      value: briefPack.product || briefPack.productRole,
    },
    { key: "brandInfo", label: "品牌資訊", value: briefPack.brandInfo },
    { key: "purchaseRisk", label: "買錯風險", value: briefPack.purchaseRisk },
    {
      key: "decisionPath",
      label: "購買判斷路徑",
      value: briefPack.decisionPath,
    },
    { key: "coreProblem", label: "主要問題", value: briefPack.coreProblem },
    {
      key: "firstJudgment",
      label: "先判斷順序",
      value: briefPack.firstJudgment,
    },
    {
      key: "entryPage",
      label: "商品承接",
      value:
        matchedProducts.length > 0
          ? matchedProducts.map(product => product.url).join("\n")
          : "原木香品牌承接",
      editable: false,
    },
    {
      key: "faqDirections",
      label: "FAQ 方向",
      value:
        briefPack.faqDirections.join("、") ||
        "用途切分、判斷順序、環境敏感度。",
    },
  ] satisfies ConfirmationRow[];
}

function splitEditableList(value: string) {
  return value
    .split(/[、；;\n]/)
    .map(item => item.trim())
    .filter(Boolean)
    .slice(0, 6);
}

function buildEditedTopicBriefPack(
  topic: WorkbenchTopic,
  rowKey: ConfirmationRowKey,
  value: string
) {
  const briefPack = normalizeArticleWorkbenchTopicBriefPack(
    topic.topicBriefPack
  );
  const next = { ...briefPack };

  if (rowKey === "person") next.person = value.trim();
  if (rowKey === "scene") next.scene = value.trim();
  if (rowKey === "product") next.product = value.trim();
  if (rowKey === "brandInfo") next.brandInfo = value.trim();
  if (rowKey === "purchaseRisk") next.purchaseRisk = value.trim();
  if (rowKey === "decisionPath") next.decisionPath = value.trim();
  if (rowKey === "coreProblem") next.coreProblem = value.trim();
  if (rowKey === "firstJudgment") next.firstJudgment = value.trim();
  if (rowKey === "entryPage") next.entryPages = [value.trim()].filter(Boolean);
  if (rowKey === "faqDirections") next.faqDirections = splitEditableList(value);

  return JSON.stringify(next);
}

function collapseGenerationLogs(
  logs: WorkbenchGenerationLog[]
): WorkbenchGenerationLog[] {
  const rowsByStep = new Map<string, WorkbenchGenerationLog>();
  const stepOrder: string[] = [];

  for (const log of logs) {
    if (!rowsByStep.has(log.stepKey)) {
      stepOrder.push(log.stepKey);
    }
    rowsByStep.set(log.stepKey, log);
  }

  return stepOrder
    .map(stepKey => rowsByStep.get(stepKey))
    .filter((log): log is WorkbenchGenerationLog => Boolean(log));
}

function getLatestAttemptVersion(logs: WorkbenchGenerationLog[]) {
  return logs.reduce(
    (version, log) => Math.max(version, Number(log.outputVersion || 0)),
    0
  );
}

function isOutputBehindLatestAttempt(
  output: WorkbenchOutput,
  logs: WorkbenchGenerationLog[]
) {
  const latestAttemptVersion = getLatestAttemptVersion(logs);
  return latestAttemptVersion > Number(output.version || 0);
}

function GenerationProgressPanel({
  logs,
  issue,
  currentStep,
  resuming,
  onRegenerate,
  regenerateDisabled,
}: {
  logs: WorkbenchGenerationLog[];
  issue?: GenerationIssue | null;
  currentStep?: string | null;
  resuming?: boolean;
  onRegenerate?: () => void;
  regenerateDisabled?: boolean;
}) {
  const visibleLogs = collapseGenerationLogs(logs).filter(
    log => log.status !== "error"
  );
  const latestActive = [...visibleLogs]
    .reverse()
    .find(log => log.status === "active");
  const latestLog = visibleLogs[visibleLogs.length - 1];
  const headline = latestActive || latestLog;

  return (
    <div className="space-y-5">
      {issue ? (
        <div className="flex items-start justify-between gap-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <AlertCircle className="mt-0.5 h-6 w-6 text-destructive" />
            <div className="min-w-0">
              <p className="text-base font-semibold text-destructive">
                {issue.title}
              </p>
              <p className="whitespace-pre-line text-sm leading-6 text-muted-foreground">
                {issue.message}
              </p>
            </div>
          </div>
          {onRegenerate ? (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 gap-2"
              onClick={onRegenerate}
              disabled={regenerateDisabled}
            >
              {regenerateDisabled ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              {issue.actionLabel}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex items-start gap-3">
          {headline?.status === "error" ? (
            <AlertCircle className="mt-0.5 h-6 w-6 text-destructive" />
          ) : !resuming && headline?.status === "done" ? (
            <Check className="mt-0.5 h-6 w-6 text-emerald-600" />
          ) : (
            <Loader2 className="mt-0.5 h-6 w-6 animate-spin text-foreground" />
          )}
          <div className="min-w-0">
            <p className="text-base font-semibold text-foreground">
              {resuming
                ? "正在繼續產文"
                : headline?.stepTitle || "正在建立產文任務"}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              {resuming
                ? getEmptyGenerationProgressMessage(currentStep)
                : headline?.message ||
                  "產文開始後，這裡會即時顯示目前做到哪一步。"}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {visibleLogs.length === 0 ? (
          <div className="rounded-md border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
            {getEmptyGenerationProgressMessage(currentStep, Boolean(issue))}
          </div>
        ) : (
          visibleLogs.map(log => (
            <div
              key={log.stepKey}
              className="grid grid-cols-[24px_minmax(0,1fr)] gap-3 rounded-md border px-3 py-3"
            >
              <div className="pt-0.5">
                {log.status === "active" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-foreground" />
                ) : log.status === "done" ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertCircle
                    className={
                      log.status === "error"
                        ? "h-4 w-4 text-destructive"
                        : "h-4 w-4 text-amber-600"
                    }
                  />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-foreground">{log.stepTitle}</p>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(log.createdAt)}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm leading-6 text-muted-foreground">
                  {log.message}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function ArticleWorkbench() {
  const { user, loading } = useAuth();
  const autoEnsureRef = useRef(false);
  const generationStartedAtRef = useRef<number | null>(null);
  const generationBaselineVersionRef = useRef(0);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [currentOutput, setCurrentOutput] = useState<WorkbenchOutput | null>(
    null
  );
  const [showConfirmationAfterRegenerate, setShowConfirmationAfterRegenerate] =
    useState(false);
  const [publicationUrl, setPublicationUrl] = useState("");
  const [copiedField, setCopiedField] = useState<"html" | "schema" | null>(
    null
  );
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(
    null
  );
  const [generationExpectedVersion, setGenerationExpectedVersion] = useState<
    number | null
  >(null);
  const [resumedDraft, setResumedDraft] =
    useState<WorkbenchGenerationDraft | null>(null);
  const [generationNow, setGenerationNow] = useState(Date.now());
  const [activeTab, setActiveTab] = useState<"writing" | "pending" | "tracking">("writing");
  const [queueNotice, setQueueNotice] = useState<string | null>(null);
  const [editingRowKey, setEditingRowKey] = useState<ConfirmationRowKey | null>(
    null
  );
  const [editingRowValue, setEditingRowValue] = useState("");

  const markGenerationStarted = (
    expectedOutputVersion: number | null = null
  ) => {
    const now = Date.now();
    generationStartedAtRef.current = now;
    setGenerationStartedAt(now);
    setGenerationExpectedVersion(expectedOutputVersion);
    return now;
  };

  const stopGenerationWaiting = () => {
    generationStartedAtRef.current = null;
    setGenerationStartedAt(null);
    setResumedDraft(null);
  };

  const clearGenerationStarted = () => {
    stopGenerationWaiting();
    setGenerationExpectedVersion(null);
  };

  const topicsQuery = trpc.articleWorkbench.list.useQuery(undefined, {
    enabled: Boolean(user) && !loading,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
  const keywordOptionsQuery = trpc.keywords.getAll.useQuery(undefined, {
    enabled: Boolean(user) && !loading,
    refetchOnWindowFocus: false,
  });
  const keywordOptions = useMemo(
    () => eligibleTopicKeywords((keywordOptionsQuery.data || []) as TrackingKeywordOption[]),
    [keywordOptionsQuery.data]
  );

  const topics = useMemo(
    () =>
      ((topicsQuery.data || []) as WorkbenchTopic[])
        .filter(topic => ACTIVE_STATUSES.includes(topic.status))
        .slice(0, 5),
    [topicsQuery.data]
  );

  const trackingTopics = useMemo(
    () =>
      ((topicsQuery.data || []) as WorkbenchTopic[])
        .filter(
          topic =>
            TRACKING_STATUSES.includes(topic.status) &&
            Boolean(topic.publicationUrl)
        )
        .sort(
          (left, right) =>
            new Date(right.publishedAt || right.suggestedAt).getTime() -
            new Date(left.publishedAt || left.suggestedAt).getTime()
        ),
    [topicsQuery.data]
  );

  const selectedTopic = useMemo(
    () =>
      ((topicsQuery.data || []) as WorkbenchTopic[]).find(
        topic => topic.id === selectedTopicId
      ) || null,
    [selectedTopicId, topicsQuery.data]
  );

  const ensureQueue = trpc.articleWorkbench.ensureQueue.useMutation({
    onSuccess: result => {
      if (result.reason) {
        setQueueNotice(result.reason);
      } else {
        setQueueNotice(null);
      }
      topicsQuery.refetch();
    },
    onError: error => setQueueNotice(error.message),
  });

  const regenerateQueue = trpc.articleWorkbench.regenerateQueue.useMutation({
    onSuccess: result => {
      setQueueNotice(result.reason || null);
      if (result.created > 0) {
        toast.success(`已重新生成 ${result.created} 題`);
      } else {
        setQueueNotice(result.reason || "目前沒有可用資料可重新生成");
      }
      topicsQuery.refetch();
    },
    onError: error => setQueueNotice(error.message),
  });

  const updateBrief = trpc.articleWorkbench.updateBrief.useMutation({
    onSuccess: () => {
      setEditingRowKey(null);
      setEditingRowValue("");
      topicsQuery.refetch();
      toast.success("已儲存");
    },
    onError: error => toast.error(error.message || "儲存失敗"),
  });

  const prepareOutput = trpc.articleWorkbench.prepareOutput.useMutation({
    onMutate: () => {
      if (!generationStartedAtRef.current) {
        markGenerationStarted();
      }
    },
    onSuccess: output => {
      const nextOutput = output as WorkbenchOutput;
      setCurrentOutput(nextOutput);
      clearGenerationStarted();
      generationDraftQuery.refetch();
      generationLogsQuery.refetch();
      topicsQuery.refetch();
    },
    onError: error => {
      if (isAbortLikeError(error)) {
        toast.warning("產文仍在進行，完成後會自動切到可複製畫面");
        generationDraftQuery.refetch();
        generationLogsQuery.refetch();
        currentOutputQuery.refetch();
        topicsQuery.refetch();
        return;
      }
      stopGenerationWaiting();
      generationDraftQuery.refetch();
      generationLogsQuery.refetch();
      toast.error(error.message || "產文流程中斷");
    },
  });

  const refreshProducts = trpc.articleWorkbench.refreshProducts.useMutation({
    onSuccess: () => {
      topicsQuery.refetch();
      toast.success("商品配對已更新");
    },
    onError: error => toast.error(error.message || "商品配對更新失敗"),
  });

  const generationDraftQuery = trpc.articleWorkbench.generationDraft.useQuery(
    { id: selectedTopicId || 1 },
    {
      enabled: Boolean(user) && Boolean(selectedTopicId),
      refetchInterval: query => {
        const draft = resolveResumingArticleGenerationDraft(
          query.state.data as WorkbenchGenerationDraft | null | undefined,
          resumedDraft
        );
        const pollingVersion = resolveArticleGenerationVersion(
          generationExpectedVersion,
          draft
        );
        return shouldPollArticleGenerationDraft({
          requestPending: prepareOutput.isPending,
          generationStarted: Boolean(generationStartedAt),
          expectedOutputVersion: pollingVersion,
          topicStatus: selectedTopic?.status,
          draftStatus: draft?.status,
          draftOutputVersion: draft?.outputVersion,
        })
          ? 1200
          : false;
      },
      refetchOnWindowFocus: false,
    }
  );
  const savedGenerationDraft = generationDraftQuery.data as
    | WorkbenchGenerationDraft
    | null
    | undefined;
  const generationDraft = resolveResumingArticleGenerationDraft(
    savedGenerationDraft,
    resumedDraft
  );
  const activeGenerationVersion = resolveArticleGenerationVersion(
    generationExpectedVersion,
    generationDraft
  );
  const isWaitingForOutput = isArticleGenerationWaiting({
    generationStarted: Boolean(generationStartedAt),
    expectedOutputVersion: activeGenerationVersion,
    draftStatus: generationDraft?.status,
    draftOutputVersion: generationDraft?.outputVersion,
    hasOutput: Boolean(currentOutput),
  });
  const shouldPollCurrentOutput = shouldPollArticleGenerationOutput({
    generationStarted: Boolean(generationStartedAt),
    expectedOutputVersion: activeGenerationVersion,
    draftStatus: generationDraft?.status,
    draftOutputVersion: generationDraft?.outputVersion,
    hasOutput: Boolean(currentOutput),
  });

  const currentOutputQuery = trpc.articleWorkbench.currentOutput.useQuery(
    {
      id: selectedTopicId || 1,
      createdAfter: getArticleGenerationOutputCreatedAfter(
        generationStartedAt,
        activeGenerationVersion
      ),
    },
    {
      enabled:
        Boolean(user) &&
        Boolean(selectedTopicId) &&
        !currentOutput &&
        !showConfirmationAfterRegenerate,
      refetchInterval:
        isWaitingForOutput || shouldPollCurrentOutput ? 1500 : false,
      refetchOnWindowFocus: false,
    }
  );

  const generationLogsQuery = trpc.articleWorkbench.generationLogs.useQuery(
    { id: selectedTopicId || 1 },
    {
      enabled: Boolean(user) && Boolean(selectedTopicId),
      refetchInterval:
        isWaitingForOutput || selectedTopic?.status === "drafting"
          ? 1200
          : false,
      refetchOnWindowFocus: false,
    }
  );
  const rawGenerationLogs = useMemo(
    () => (generationLogsQuery.data || []) as WorkbenchGenerationLog[],
    [generationLogsQuery.data]
  );

  const archiveTopic = trpc.articleWorkbench.replace.useMutation({
    onSuccess: result => {
      setQueueNotice(result.reason || null);
      if (!result.created) {
        toast.info(result.reason || "沒有可替換的完整新題，原題保留");
        return;
      }
      toast.success("已換一題");
      setSelectedTopicId(null);
      setCurrentOutput(null);
      setPublicationUrl("");
      topicsQuery.refetch();
    },
    onError: error => toast.error(error.message),
  });

  const publishTopic = trpc.articleWorkbench.publish.useMutation({
    onSuccess: () => {
      toast.success("已開始追蹤");
      setSelectedTopicId(null);
      setCurrentOutput(null);
      setPublicationUrl("");
      setActiveTab("tracking");
      topicsQuery.refetch();
      ensureQueue.mutate({ targetCount: 5 });
    },
    onError: error => toast.error(error.message),
  });

  useEffect(() => {
    if (!user || loading) return;
    if (autoEnsureRef.current) return;
    autoEnsureRef.current = true;
    ensureQueue.mutate({ targetCount: 5 });
  }, [user, loading]);

  useEffect(() => {
    if (!selectedTopic) return;
    setCurrentOutput(null);
    setShowConfirmationAfterRegenerate(false);
    setPublicationUrl("");
    setEditingRowKey(null);
    setEditingRowValue("");
    generationBaselineVersionRef.current = 0;
    clearGenerationStarted();
  }, [selectedTopic?.id]);

  useEffect(() => {
    if (!isWaitingForOutput && selectedTopic?.status !== "drafting") return;
    const timer = window.setInterval(() => setGenerationNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isWaitingForOutput, selectedTopic?.status]);

  useEffect(() => {
    if (!generationStartedAt) return;
    if (generationDraft?.status !== "error") return;
    if (
      !isArticleGenerationVersionMatch(
        activeGenerationVersion,
        generationDraft.outputVersion
      )
    ) {
      return;
    }
    stopGenerationWaiting();
  }, [
    generationDraft?.status,
    generationDraft?.outputVersion,
    activeGenerationVersion,
    generationStartedAt,
  ]);

  useEffect(() => {
    if (
      !currentOutputQuery.data ||
      currentOutput ||
      showConfirmationAfterRegenerate
    )
      return;
    const output = currentOutputQuery.data as WorkbenchOutput;
    if (isOutputBehindLatestAttempt(output, rawGenerationLogs)) return;
    if (
      !isOutputFreshForCurrentGeneration(
        output,
        generationStartedAtRef.current,
        activeGenerationVersion,
        generationDraft?.status === "complete"
      )
    )
      return;
    setCurrentOutput(output);
    clearGenerationStarted();
  }, [
    currentOutputQuery.data,
    currentOutput,
    activeGenerationVersion,
    generationDraft?.status,
    generationStartedAt,
    rawGenerationLogs,
    showConfirmationAfterRegenerate,
  ]);

  const handleCopy = async (type: "html" | "schema") => {
    if (!currentOutput) return;
    const textToCopy =
      type === "html"
        ? currentOutput.htmlContent
        : formatSchemaScript(currentOutput.schemaJson);
    await navigator.clipboard.writeText(textToCopy);
    setCopiedField(type);
    setTimeout(() => setCopiedField(null), 1800);
    toast.success(type === "html" ? "HTML 已複製" : "Schema 已複製");
  };

  const handlePublish = () => {
    if (!selectedTopic || !publicationUrl.trim()) return;
    publishTopic.mutate({
      id: selectedTopic.id,
      publicationUrl: publicationUrl.trim(),
    });
  };

  const handleRegenerateOutput = () => {
    if (!selectedTopic) return;
    generationBaselineVersionRef.current = Math.max(
      Number(currentOutput?.version || 0),
      Number(currentOutputQuery.data?.version || 0),
      Number(generationDraft?.outputVersion || 0),
      getLatestAttemptVersion(rawGenerationLogs)
    );
    setCurrentOutput(null);
    setShowConfirmationAfterRegenerate(true);
    setPublicationUrl("");
    setCopiedField(null);
    clearGenerationStarted();
    refreshProducts.mutate({ id: selectedTopic.id });
  };

  const handleStartGenerate = () => {
    if (!selectedTopic) return;
    const expectedOutputVersion = getNextArticleGenerationVersion(
      generationBaselineVersionRef.current,
      Number(generationDraft?.outputVersion || 0),
      Number(currentOutputQuery.data?.version || 0),
      getLatestAttemptVersion(rawGenerationLogs)
    );
    setShowConfirmationAfterRegenerate(false);
    markGenerationStarted(expectedOutputVersion);
    setCurrentOutput(null);
    prepareOutput.mutate({ id: selectedTopic.id, forceNew: true });
  };

  const handleContinueGenerate = () => {
    if (!selectedTopic) return;
    const resumeVersion = Number(
      generationDraft?.outputVersion ||
        getLatestAttemptVersion(rawGenerationLogs) ||
        0
    );
    setResumedDraft(savedGenerationDraft || null);
    markGenerationStarted(resumeVersion || null);
    setCurrentOutput(null);
    prepareOutput.mutate({ id: selectedTopic.id, forceNew: false });
  };

  const handleChangeTopic = () => {
    if (!selectedTopic) return;
    archiveTopic.mutate({ id: selectedTopic.id });
  };

  const handleRetryQueue = () => {
    setQueueNotice(null);
    topicsQuery.refetch();
    ensureQueue.mutate({ targetCount: 5 });
  };

  const handleStartEditRow = (row: ConfirmationRow) => {
    setEditingRowKey(row.key);
    if (row.key === "sourceKeyword") {
      setEditingRowValue(String(selectedTopic?.sourceKeywordId || ""));
      return;
    }
    setEditingRowValue(row.value);
  };

  const handleCancelEditRow = () => {
    setEditingRowKey(null);
    setEditingRowValue("");
  };

  const handleSaveEditRow = () => {
    if (!selectedTopic || !editingRowKey) return;

    if (editingRowKey === "sourceKeyword") {
      const selectedKeyword = keywordOptions.find(
        keyword => String(keyword.id) === editingRowValue
      );
      if (!selectedKeyword) {
        toast.error("請選擇來源關鍵字");
        return;
      }
      updateBrief.mutate({
        id: selectedTopic.id,
        expectedUpdatedAt: new Date(selectedTopic.updatedAt).toISOString(),
        title: selectedTopic.title,
        mainKeyword: selectedKeyword.keyword,
        sourceKeywordId: selectedKeyword.id,
        targetPage: selectedTopic.targetPage || undefined,
        emphasis: selectedTopic.emphasis || undefined,
        nextAction: selectedTopic.nextAction || undefined,
        topicBriefPack: selectedTopic.topicBriefPack || undefined,
      });
      return;
    }

    const topicBriefPack = buildEditedTopicBriefPack(
      selectedTopic,
      editingRowKey,
      editingRowValue
    );
    const nextTargetPage =
      editingRowKey === "entryPage"
        ? editingRowValue.trim()
        : selectedTopic.targetPage || undefined;
    updateBrief.mutate({
      id: selectedTopic.id,
      expectedUpdatedAt: new Date(selectedTopic.updatedAt).toISOString(),
      title: selectedTopic.title,
      mainKeyword: selectedTopic.mainKeyword,
      sourceKeywordId: selectedTopic.sourceKeywordId ?? undefined,
      targetPage: nextTargetPage || undefined,
      emphasis: selectedTopic.emphasis || undefined,
      nextAction: selectedTopic.nextAction || undefined,
      topicBriefPack,
    });
  };

  if (selectedTopic) {
    const isPublishedTopic = TRACKING_STATUSES.includes(selectedTopic.status);
    const isPreparing = isWaitingForOutput;
    const isRegenerating = prepareOutput.isPending && Boolean(currentOutput);
    const confirmationRows = buildConfirmationRows(selectedTopic);
    const generationLogs = selectArticleGenerationLogs(
      rawGenerationLogs,
      activeGenerationVersion,
      generationStartedAt,
      prepareOutput.isPending
    );
    const isCurrentDraftError =
      !showConfirmationAfterRegenerate &&
      generationDraft?.status === "error" &&
      isArticleGenerationVersionMatch(
        activeGenerationVersion,
        generationDraft.outputVersion
      );
    const generationIssue = isCurrentDraftError
      ? {
          title: `系統錯誤：停在「${generationStepLabel(generationDraft.currentStep)}」`,
          message:
            generationDraft.errorMessage ||
            "產文服務發生錯誤，已完成的內容仍保留。",
          actionLabel: "繼續產文",
        }
      : null;
    const shouldShowGenerationPanel = shouldShowArticleGenerationProgress({
      waiting: isPreparing,
      hasOutput: Boolean(currentOutput),
      draftError: Boolean(isCurrentDraftError),
    });
    const displayOutput = isCurrentDraftError ? null : currentOutput;
    const knowledgeEvidenceResult =
      parseWorkbenchKnowledgeEvidence(displayOutput);
    const knowledgeEvidence = knowledgeEvidenceResult.evidence;

    const citationStatus = getCitationStatus(selectedTopic);

    return (
      <DashboardLayout>
        <main className="min-h-screen bg-background">
          <section className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-5 py-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 border-b pb-5">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                onClick={() => {
                  setSelectedTopicId(null);
                  setCurrentOutput(null);
                  setPublicationUrl("");
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                返回
              </Button>
              <Badge variant="outline" className="gap-1">
                <CalendarClock className="h-3.5 w-3.5" />
                {formatDate(selectedTopic.suggestedAt)}
              </Badge>
            </div>

            <div className="grid gap-4 py-5">
              <Card>
                <CardContent className="space-y-3 p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">
                      {STATUS_LABELS[selectedTopic.status] || "待發布"}
                    </Badge>
                    <Badge variant="outline">
                      /keywords：
                      {selectedTopic.sourceKeyword || selectedTopic.mainKeyword}
                    </Badge>
                  </div>
                  <h1 className="text-xl font-semibold tracking-normal">
                    {selectedTopic.title}
                  </h1>
                  <ArticleTopicPlanningInfo json={selectedTopic.topicBriefPack} />
                  {isPublishedTopic && selectedTopic.publicationUrl ? (
                    <a
                      href={selectedTopic.publicationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 items-center gap-2 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      <ExternalLink className="h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {selectedTopic.publicationUrl}
                      </span>
                    </a>
                  ) : null}
                </CardContent>
              </Card>

              {isPublishedTopic ? (
                <Card>
                  <CardContent className="grid gap-3 p-5 sm:grid-cols-2">
                    <PublicationTrackingCard topicId={selectedTopic.id} url={selectedTopic.publicationUrl} json={selectedTopic.publicationTrackingJson} />
                    <div className="rounded-md border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">AIO / LLM 引用</p>
                        <Badge
                          variant="outline"
                          className={statusBadgeClass(citationStatus.tone)}
                        >
                          {citationStatus.label}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {citationStatus.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              {isPublishedTopic &&
              !currentOutput &&
              !shouldShowGenerationPanel ? (
                <Card>
                  <CardContent className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
                    {currentOutputQuery.isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCode2 className="h-4 w-4" />
                    )}
                    {currentOutputQuery.isLoading
                      ? "載入原本產出的 HTML 與 Schema"
                      : "這篇文章沒有找到可複製內容"}
                  </CardContent>
                </Card>
              ) : null}

              {!isPublishedTopic &&
              !currentOutput &&
              !shouldShowGenerationPanel ? (
                <Card>
                  <CardContent className="space-y-5 p-5">
                    {(() => {
                      const matchedProducts =
                        normalizeArticleWorkbenchTopicBriefPack(
                          selectedTopic.topicBriefPack
                        ).matchedProducts;
                      return (
                        <div className="space-y-3 rounded-md border bg-muted/20 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-medium">自動配對商品</p>
                            <Badge variant="outline">
                              {matchedProducts.length > 0
                                ? `${matchedProducts.length} 個有庫存商品`
                                : "原木香品牌承接"}
                            </Badge>
                          </div>
                          {matchedProducts.length > 0 ? (
                            <div className="grid gap-3 sm:grid-cols-3">
                              {matchedProducts.map(product => (
                                <a
                                  key={product.id}
                                  href={product.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="overflow-hidden rounded-md border bg-background transition-colors hover:border-foreground/40"
                                >
                                  {product.imageUrl ? (
                                    <img
                                      src={product.imageUrl}
                                      alt={product.title}
                                      className="h-28 w-full object-cover"
                                    />
                                  ) : null}
                                  <span className="block space-y-1 p-3 text-sm">
                                    <span className="block font-medium leading-5">
                                      {product.title}
                                    </span>
                                    <span className="block text-muted-foreground">
                                      {product.salePrice ||
                                        product.price ||
                                        "價格未提供"}
                                    </span>
                                    <span className="block text-xs text-muted-foreground">
                                      {product.productType ||
                                        product.googleProductCategory}
                                    </span>
                                  </span>
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm leading-6 text-muted-foreground">
                              沒有完全對應且有庫存的商品，文章將只談原木香品牌，不會硬連商品。
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    <div className="overflow-hidden rounded-md border">
                      <table className="w-full text-sm">
                        <tbody>
                          {confirmationRows.map(row => {
                            const isEditing =
                              row.editable !== false &&
                              editingRowKey === row.key;
                            return (
                              <tr
                                key={row.key}
                                className="border-b last:border-b-0"
                              >
                                <th className="w-32 bg-muted/60 px-3 py-3 text-left align-top font-medium text-foreground">
                                  {row.label}
                                </th>
                                <td className="px-3 py-3 leading-6 text-muted-foreground">
                                  {isEditing ? (
                                    row.key === "sourceKeyword" ? (
                                      <Select
                                        value={editingRowValue}
                                        onValueChange={setEditingRowValue}
                                      >
                                        <SelectTrigger className="min-h-10">
                                          <SelectValue placeholder="選擇來源關鍵字" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {keywordOptions.map(keyword => (
                                            <SelectItem
                                              key={keyword.id}
                                              value={String(keyword.id)}
                                            >
                                              {keyword.keyword}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <Textarea
                                        value={editingRowValue}
                                        onChange={event =>
                                          setEditingRowValue(event.target.value)
                                        }
                                        className="min-h-20 resize-y"
                                      />
                                    )
                                  ) : (
                                    row.value
                                  )}
                                </td>
                                <td className="w-24 px-3 py-3 align-top">
                                  {isEditing ? (
                                    <div className="flex justify-end gap-1">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={handleSaveEditRow}
                                        disabled={
                                          updateBrief.isPending ||
                                          row.editable === false
                                        }
                                        aria-label={`儲存${row.label}`}
                                      >
                                        {updateBrief.isPending ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <Save className="h-4 w-4" />
                                        )}
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={handleCancelEditRow}
                                        disabled={updateBrief.isPending}
                                        aria-label={`取消編輯${row.label}`}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => handleStartEditRow(row)}
                                        disabled={updateBrief.isPending}
                                        aria-label={`編輯${row.label}`}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                      <Button
                        size="lg"
                        className="h-12 gap-2"
                        onClick={handleStartGenerate}
                        disabled={
                          prepareOutput.isPending || archiveTopic.isPending
                        }
                      >
                        <Sparkles className="h-4 w-4" />
                        開始產生文章
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="lg"
                            variant="outline"
                            className="h-12 gap-2"
                            disabled={
                              prepareOutput.isPending || archiveTopic.isPending || selectedTopic.status !== "suggested"
                            }
                          >
                            <Shuffle className="h-4 w-4" />
                            換一題
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>換掉這題？</AlertDialogTitle>
                            <AlertDialogDescription>
                              新題準備完成後才會替換這題，原有文章與原稿會保留。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>取消</AlertDialogCancel>
                            <AlertDialogAction onClick={handleChangeTopic}>
                              換一題
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              {shouldShowGenerationPanel || displayOutput ? (
                <Card>
                  <CardContent className="space-y-4 p-5">
                    {shouldShowGenerationPanel ? (
                      <div className="min-h-64">
                        <GenerationProgressPanel
                          logs={generationLogs}
                          issue={generationIssue}
                          resuming={generationDraft !== savedGenerationDraft}
                          currentStep={getArticleGenerationCurrentStep(
                            activeGenerationVersion,
                            generationDraft?.outputVersion,
                            generationDraft?.currentStep
                          )}
                          onRegenerate={
                            generationDraft?.status === "error"
                              ? handleContinueGenerate
                              : handleRegenerateOutput
                          }
                          regenerateDisabled={prepareOutput.isPending}
                        />
                        {generationDraft?.status === "error" ? (
                          <div className="mt-4 flex justify-end">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="gap-2"
                                  disabled={
                                    prepareOutput.isPending ||
                                    refreshProducts.isPending
                                  }
                                >
                                  <RotateCcw className="h-4 w-4" />
                                  重新產文
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    回到題目確認後重新產文？
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    系統會捨棄這個未完成版本、重新抓取 Product
                                    Feed，回到題目確認頁；確認後再按「開始產生文章」。
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>取消</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={handleRegenerateOutput}
                                  >
                                    重新產文
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {displayOutput ? (
                      <>
                        {knowledgeEvidenceResult.status === "invalid" ? (
                          <div className="rounded-md border bg-muted/20 p-4 text-sm">
                            <p className="font-medium text-destructive">
                              直播知識來源資料無法解析
                            </p>
                            <p className="mt-1 text-muted-foreground">
                              這份成品的來源追溯資料已損壞，不能視為「沒有使用直播知識」。
                            </p>
                          </div>
                        ) : knowledgeEvidence.length > 0 ? (
                          <div className="overflow-hidden rounded-md border bg-background text-sm">
                            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
                              <p className="font-semibold">本文引用的知識</p>
                              <Badge variant="outline">
                                {knowledgeEvidence.length} 張
                              </Badge>
                            </div>
                            <ol className="divide-y">
                              {knowledgeEvidence.map((evidence, index) => (
                                <li
                                  key={evidence.id}
                                  className="flex items-start gap-3 px-4 py-3"
                                >
                                  <span className="mt-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                                    {String(index + 1).padStart(2, "0")}
                                  </span>
                                  <p className="font-medium leading-6 text-foreground">
                                    {evidence.title}
                                  </p>
                                </li>
                              ))}
                            </ol>
                            <details className="border-t">
                              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                                查看來源
                              </summary>
                              <div className="space-y-4 border-t bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
                                {knowledgeEvidence.map(evidence => (
                                  <div key={evidence.id} className="space-y-1">
                                    <p className="font-medium text-foreground">
                                      {evidence.title}
                                    </p>
                                    <p>
                                      {evidence.sourceVideo}｜
                                      {evidence.sourceTime}
                                    </p>
                                    {evidence.sourceSegments?.length > 0 ? (
                                      <p>
                                        有效片段：
                                        {evidence.sourceSegments.join("、")}
                                      </p>
                                    ) : null}
                                    <ul className="space-y-1 text-xs">
                                      {evidence.sourceEvidence.map(source => (
                                        <li
                                          key={`${source.segment}-${source.sourceTime}-${source.sourcePath}`}
                                        >
                                          <p>
                                            {source.segment}｜
                                            {source.sourceVideo}｜
                                            {source.sourceTime}
                                          </p>
                                          <p className="break-all">
                                            {source.sourcePath}
                                          </p>
                                        </li>
                                      ))}
                                    </ul>
                                    <p className="break-all text-xs">
                                      {evidence.sourcePath}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </div>
                        ) : (
                          <div className="rounded-md border bg-muted/20 p-4 text-sm">
                            <p className="font-medium">
                              本篇沒有使用可追溯的直播知識
                            </p>
                            <p className="mt-1 text-muted-foreground">
                              文章維持原本商品資料流程，不會把商品頁描述標成直播說法。
                            </p>
                          </div>
                        )}
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Button
                            size="lg"
                            className="h-14 gap-2"
                            onClick={() => handleCopy("html")}
                            disabled={isRegenerating}
                          >
                            {copiedField === "html" ? (
                              <Check className="h-5 w-5" />
                            ) : (
                              <Copy className="h-5 w-5" />
                            )}
                            複製 HTML
                          </Button>
                          <Button
                            size="lg"
                            variant="outline"
                            className="h-14 gap-2"
                            onClick={() => handleCopy("schema")}
                            disabled={isRegenerating}
                          >
                            {copiedField === "schema" ? (
                              <Check className="h-5 w-5" />
                            ) : (
                              <Copy className="h-5 w-5" />
                            )}
                            複製 Schema
                          </Button>
                        </div>

                        <details className="rounded-md border bg-muted/30 p-3 text-sm">
                          <summary className="cursor-pointer font-medium">
                            檢查內容
                          </summary>
                          <div className="mt-3 grid gap-3">
                            <pre className="max-h-48 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                              {displayOutput.htmlContent}
                            </pre>
                            <pre className="max-h-48 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                              {displayOutput.schemaJson}
                            </pre>
                          </div>
                        </details>
                      </>
                    ) : null}
                  </CardContent>
                </Card>
              ) : null}

              {displayOutput && !isPublishedTopic ? (
                <Card>
                  <CardContent className="space-y-3 p-5">
                    <Input
                      value={publicationUrl}
                      onChange={event => setPublicationUrl(event.target.value)}
                      placeholder="貼上原木香官網文章網址"
                      inputMode="url"
                    />
                    <Button
                      className="h-12 w-full gap-2"
                      onClick={handlePublish}
                      disabled={
                        !displayOutput ||
                        !publicationUrl.trim() ||
                        publishTopic.isPending
                      }
                    >
                      {publishTopic.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                      開始追蹤
                    </Button>
                  </CardContent>
                </Card>
              ) : null}

              {displayOutput && !isPublishedTopic ? (
                <div className="flex justify-end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="gap-2"
                        disabled={
                          prepareOutput.isPending || refreshProducts.isPending
                        }
                      >
                        <RotateCcw className="h-4 w-4" />
                        重新產文
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          回到題目確認後重新產文？
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          系統會清除目前畫面內容、重新抓取 Product
                          Feed，回到題目確認頁；確認後再按「開始產生文章」。
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>取消</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRegenerateOutput}>
                          重新產文
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : null}
            </div>
          </section>
        </main>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <main className="min-h-screen bg-background">
        <section className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 py-6 lg:px-8">
          <Tabs
            value={activeTab}
            onValueChange={value =>
              setActiveTab(value as "writing" | "pending" | "tracking")
            }
            className="flex-1 gap-0"
          >
            <div className="flex flex-col gap-4 border-b pb-5 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-normal">
                  文章題目工作台
                </h1>
                <p className="text-sm text-muted-foreground">
                  五題產文與已發布文章分開管理
                </p>
              </div>
              <TabsList className="grid w-full grid-cols-3 md:w-auto">
                <TabsTrigger value="writing">現在要寫</TabsTrigger>
                <TabsTrigger value="pending">待發布</TabsTrigger>
                <TabsTrigger value="tracking">已發布追蹤</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="writing" className="mt-0">
              <div className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold tracking-normal">
                    現在要寫的五題
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {topics.length}/5 題 · 每題都保留出題時間
                  </p>
                </div>
                <Button
                  className="w-full gap-2 md:w-auto"
                  onClick={() => regenerateQueue.mutate()}
                  disabled={regenerateQueue.isPending || ensureQueue.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                  {regenerateQueue.isPending ? "重新生成中" : "重新生成"}
                </Button>
              </div>

              <div className="grid flex-1 grid-rows-[auto] gap-3 pb-5">
                {topicsQuery.isLoading ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">
                      載入題目中
                    </CardContent>
                  </Card>
                ) : null}

                {!topicsQuery.isLoading && topics.length === 0 ? (
                  <Card>
                    <CardContent className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                      <div className="flex min-w-0 items-start gap-3">
                        <AlertCircle className="mt-0.5 h-5 w-5 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">
                            {topicsQuery.error
                              ? "題目資料讀取失敗"
                              : "還沒有可寫題目"}
                          </p>
                          <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            {topicsQuery.error
                              ? topicsQuery.error.message
                              : queueNotice ||
                                (ensureQueue.isPending ? "正在依競品佔位整理出題來源與內容角度…" : "請到關鍵字管理確認最新競品佔位結果，系統會依有來源的內容角度準備最多五題。")}
                          </p>
                        </div>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 md:flex md:justify-end">
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={handleRetryQueue}
                          disabled={ensureQueue.isPending}
                        >
                          {ensureQueue.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          重新讀取
                        </Button>
                        <Button asChild className="gap-2">
                          <a href="/keywords">去關鍵字管理</a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}

                {topics.length > 0 && queueNotice ? <p role="status" className="rounded-md border p-3 text-sm">{queueNotice}</p> : null}

                {topics.map((topic, index) => (
                  <Card
                    key={topic.id}
                    className="cursor-pointer border-border/80 transition-colors hover:border-foreground/30"
                    onClick={() => setSelectedTopicId(topic.id)}
                  >
                    <CardContent className="grid gap-4 p-5 md:grid-cols-[56px_minmax(0,1fr)] md:items-center">
                      <div className="flex h-11 w-11 items-center justify-center rounded-md border bg-muted text-base font-semibold">
                        {index + 1}
                      </div>
                      <div className="min-w-0 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="gap-1">
                            <CalendarClock className="h-3.5 w-3.5" />
                            {formatDate(topic.suggestedAt)}
                          </Badge>
                          <Badge variant="secondary">
                            {STATUS_LABELS[topic.status] || "待發布"}
                          </Badge>
                          <Badge variant="outline">
                            /keywords：
                            {topic.sourceKeyword || topic.mainKeyword}
                          </Badge>
                        </div>
                        <h2 className="text-lg font-semibold tracking-normal">
                          {topic.title}
                        </h2>
                        <ArticleTopicPlanningInfo json={topic.topicBriefPack} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="pending" className="mt-0">
              <PendingPublicationList
                topics={(topicsQuery.data || []) as WorkbenchTopic[]}
                loading={topicsQuery.isLoading}
                error={topicsQuery.error?.message}
                onOpen={setSelectedTopicId}
              />
            </TabsContent>

            <TabsContent value="tracking" className="mt-0">
              <div className="py-5">
                <h2 className="text-xl font-semibold tracking-normal">
                  已發布追蹤
                </h2>
                <p className="text-sm text-muted-foreground">
                  {trackingTopics.length} 篇 ·
                  可查看官網文章、收錄狀態、引用狀態與原本 HTML / Schema
                </p>
              </div>

              <div className="grid gap-3 pb-5">
                {topicsQuery.isLoading ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">
                      載入追蹤文章中
                    </CardContent>
                  </Card>
                ) : null}

                {!topicsQuery.isLoading && trackingTopics.length === 0 ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">
                      目前沒有已發布追蹤文章
                    </CardContent>
                  </Card>
                ) : null}

                {trackingTopics.map(topic => {

                  const citationStatus = getCitationStatus(topic);
                  return (
                    <Card key={topic.id} className="border-border/80">
                      <CardContent className="space-y-4 p-5">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary">
                                {STATUS_LABELS[topic.status] || "追蹤中"}
                              </Badge>
                              <Badge variant="outline" className="gap-1">
                                <CalendarClock className="h-3.5 w-3.5" />
                                {formatDate(topic.publishedAt)}
                              </Badge>
                            </div>
                            <h3 className="text-lg font-semibold tracking-normal">
                              {topic.title}
                            </h3>
                            {topic.publicationUrl ? (
                              <a
                                href={topic.publicationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex max-w-full items-center gap-2 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                              >
                                <ExternalLink className="h-4 w-4 shrink-0" />
                                <span className="truncate">
                                  {topic.publicationUrl}
                                </span>
                              </a>
                            ) : null}
                          </div>
                          <Button
                            variant="outline"
                            className="shrink-0 gap-2"
                            onClick={() => setSelectedTopicId(topic.id)}
                          >
                            <FileCode2 className="h-4 w-4" />
                            查看與複製
                          </Button>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <PublicationTrackingCard topicId={topic.id} url={topic.publicationUrl} json={topic.publicationTrackingJson} />
                          <div className="rounded-md border p-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium">
                                AIO / LLM 引用
                              </p>
                              <Badge
                                variant="outline"
                                className={statusBadgeClass(
                                  citationStatus.tone
                                )}
                              >
                                {citationStatus.label}
                              </Badge>
                            </div>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">
                              {citationStatus.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </section>
      </main>
    </DashboardLayout>
  );
}
